import { inject } from "mobx-react";
import React from "react";
import type { Store } from "../../../client/stores";
import { AnalyticsEvent } from "../../../client/stores";
import { activateEmbeds, vendorRefreshFns } from "../lib/embed";
import "./Embed.style.scss";
import { EmbedLegacyItem } from "./EmbedLegacyItem.component";

export interface EmbedAttributes {
    height: string | number | null;
    width: string | number;

    [key: string]: any;
}

export interface EmbedLegacyProps {
    attributes?: EmbedAttributes;
    embed_type: string;
    provider?: string;
    html: string;
    rendering_hint?: string;
    url?: string;
    ampLibs?: Set<string>;
    store?: Store;
}

interface EmbedLegacyState {
    is_viewable: boolean;
    is_iframe: boolean;
}

let counter = 0;

@inject("store")
export default class EmbedLegacy extends React.Component<EmbedLegacyProps, EmbedLegacyState> {
    private observer?: IntersectionObserver;
    private contentRef: React.RefObject<HTMLDivElement>;

    mounted = false;

    constructor(props: EmbedLegacyProps) {
        super(props);

        this.state = {
            is_viewable: false,
            is_iframe: /iframe/i.test(this.props.html),
        };

        // So we can feed a DOM ref to the intersection observer
        this.contentRef = React.createRef();
    }

    private handleIntersect = (entries: IntersectionObserverEntry[]) => {
        const [target] = entries;

        if (target && target.intersectionRatio > 0 && this.mounted) {
            this.setState({ is_viewable: true });
            if (this.observer) {
                this.observer.disconnect();
            }
        }
    };

    private refreshVendors = () => vendorRefreshFns[this.props.provider || ""]?.();

    private analyticsHandler = (event: AnalyticsEvent) => {
        if (event.context === "continue_reading") {
            this.refreshVendors();
        }
    };

    componentDidMount() {
        this.mounted = true;
        if (!this.contentRef.current) {
            return;
        }
        const options = {
            rootMargin: "0px 0px 25px 0px",
        };

        this.observer = new IntersectionObserver(e => this.handleIntersect(e), options);
        this.observer.observe(this.contentRef.current);

        this.props.store?.onAnalyticsAction.subscribe(this.analyticsHandler);
    }

    componentWillUnmount() {
        this.mounted = false;
        counter = 0;
        if (this.observer) {
            this.observer.disconnect();
        }

        this.props.store?.onAnalyticsAction.unsubscribe(this.analyticsHandler);
    }

    componentDidUpdate() {
        if (this.state.is_viewable) {
            this.refreshVendors();
        }
        if (this.contentRef.current) {
            activateEmbeds(this.contentRef.current);
        }
    }

    render() {
        return (
            <div className="component-embed" ref={this.contentRef}>
                <EmbedLegacyItem counter={++counter} {...this.props} />
            </div>
        );
    }
}
